import { LINK_REGEX, USERNAME_REGEX, TAG_NAME_REGEX, DELIMITER_REGEX, UID_REGEX } from 'constants/feed'
import { ComposeType, Mention, FeedUserUid, CommentUid, PostUid } from 'interfaces/feed'
import * as find from 'lodash/find'
import { mentionize } from 'helpers/feedCommonHelpers'
import * as memoize from 'lodash/memoize'
import * as uniq from 'lodash/uniq'
import { SocialInsightUid } from 'interfaces/socialInsights'

export const getReplyComposeId = (commentUid: CommentUid) => `reply${ commentUid }`
export const getEditPostComposeId = (postUid: PostUid) => `editPost${ postUid }`
export const getEditCommentComposeId = (postUid: PostUid) => `editComment${ postUid }`
export const getCreateReviewComposeId = (uid: SocialInsightUid) => `review${ uid }`

export const extractTag = (text: string, selectionEnd: number, selectionStart: number) => {
  const isSelection = selectionEnd !== selectionStart
  const isTextStart = selectionStart <= 0
  const isTextEnd = selectionEnd >= text.length
  const isSpaceBefore = !isTextStart && text[selectionStart - 1] === ' '
  const isEndOfLineBefore = !isTextStart && text[selectionStart - 1] === '\n'
  const isSpaceAfter = !isTextEnd && text[selectionEnd] === ' '

  const tagPrefix = !isTextStart && !isSpaceBefore && !isEndOfLineBefore ? ' ' : ''
  const tagPostfix = isSelection && !isTextEnd && !isSpaceAfter ? ' ' : ''
  const tagName = isSelection ? text.substring(selectionStart, selectionEnd) : ''

  return `${ tagPrefix }#${ tagName }${ tagPostfix }`
}

export type DecorationType = 'link' | 'tag' | 'mention'
export type Decoration = {
  start: number
  end: number
  type: DecorationType
}

type ParseCallback = (param: Decoration) => void
const handleMatch = (cb: ParseCallback, rawMatch: string, mention: string | undefined, tag: string | undefined, matchIndex: number) => {
  const hasDelimiter = (mention || tag)
  const delimiterSize = hasDelimiter && matchIndex !== 0 ? 1 : 0
  const start = matchIndex + delimiterSize
  const end = matchIndex + rawMatch.length
  cb({
    start,
    end,
    type:
      mention ? 'mention' :
        tag ? 'tag' :
          'link',
  })
  return rawMatch
}
const COMMENT_DECORATIONS_REGEX = new RegExp(`(${ DELIMITER_REGEX.source }@(?:${ USERNAME_REGEX.source })*)|${ LINK_REGEX.source }`, 'gi')
const commentParser = (text: string, cb: ParseCallback) => {
  text.replace(COMMENT_DECORATIONS_REGEX, (rawMatch, mention, matchIndex) => handleMatch(cb, rawMatch, mention, undefined, matchIndex))
}

const POST_DECORATIONS_REGEX = new RegExp(`${ COMMENT_DECORATIONS_REGEX.source }|(${ DELIMITER_REGEX.source }#(?:${ TAG_NAME_REGEX.source })*)`, 'gi')
const postParser = (text: string, cb: ParseCallback) => {
  text.replace(POST_DECORATIONS_REGEX, (rawMatch, mention, tag, matchIndex) => handleMatch(cb, rawMatch, mention, tag, matchIndex))
}

export const parseText = memoize((text: string, type: ComposeType) => {
  const decorations: Decoration[] = []

  const parser = type === 'post' ? postParser : commentParser
  parser(text, (decoration) => decorations.push(decoration))
  return decorations
}, (text: string, type: ComposeType) => `${ type }-${ text }`)

export const parseTags = memoize((text: string) => {
  return uniq(
    parseText(text, 'post')
      .filter((decoration) => decoration.type === 'tag')
      .map((decoration) => text.substring(decoration.start + 1, decoration.end))
      .filter((tag) => !!tag)
  )
}, (text: string) => text)

/** Usernames in `text` that match known mentions (order follows appearance; duplicates allowed). */
export const extractMentions = (text: string, mentions: Mention[]): Mention[] => {
  const usedMentions: Mention[] = []
  const trimmedText = text.trim()
  trimmedText.replace(new RegExp(`@(${ USERNAME_REGEX.source })`, 'g'), (match, username) => {
    const mention = find(mentions, { username })
    if (mention) {
      usedMentions.push(mention)
    }
    return match
  })
  return usedMentions
}

export const preparePostForApi = (text: string, mentions: Mention[]) => {
  const trimmedText = text.trim()
  const usedMentions = extractMentions(text, mentions)
  const nextText = trimmedText.replace(new RegExp(`@(${ USERNAME_REGEX.source })`, 'g'), (match, username) => {
    const mention = find(mentions, { username })
    if (mention) {
      return mentionize(mention.uid)
    }
    return match
  })

  return {
    text: nextText,
    mentions: usedMentions,
  }
}

/** Payload for compose submit: trimmed text with @username → @uid, plus mentions used. */
export const buildPostOutput = (text: string, mentions: Mention[]) => preparePostForApi(text, mentions)

export const preparePostForDisplay = (text: string, mentions: Record<FeedUserUid, { username: string }>) => {
  return text.replace(new RegExp(`@(${ UID_REGEX.source })`, 'g'), (match, uid) => {
    const mention = mentions[uid]
    if (mention) {
      return mentionize(mention.username)
    }
    return match
  })
}

type GetAllowedButtonsProps = {
  isEdit: boolean | null
  isMobile: boolean
  replyCommentUid: CommentUid | null | undefined
  isOpportunityReview: boolean | undefined
}
export const getAllowedButtons = (props: GetAllowedButtonsProps) => {
  const { isEdit, isMobile, replyCommentUid, isOpportunityReview } = props
  const isReply = !!replyCommentUid && !isOpportunityReview
  const isPost = !isReply && !isOpportunityReview

  const cancel = !!isEdit && !isOpportunityReview
  const save = cancel
  const post = !isEdit && isPost
  const reply = !isEdit && isReply
  return {
    emoji: !isMobile,
    cancel,
    save,
    reply,
    post,
  }
}
